import FocusTrap from "focus-trap-react";
import * as React from "react";
import styled from "styled-components";

import { Portal } from "@/components/shared/portal";

import { swallowEvent } from "../event-boundary/event-boundary-component";

type TPlacement = "top-start";

type Props = {
  body: React.ReactNode;
  children: (props: {
    innerRef: React.MutableRefObject<HTMLElement | null>;
    triggerProps: {
      onClick: (event: React.MouseEvent<HTMLElement>) => void;
      "aria-expanded": boolean;
      "aria-haspopup": "menu";
    };
  }) => React.ReactNode;
  placement?: TPlacement;
  label: string;
};

export default function Floating(props: Props) {
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const focusTrapRef = React.useRef<HTMLDivElement | null>(null);
  const [state, setState] = React.useState<"showing" | "hidden">("hidden");

  const handleClick = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event?.preventDefault();
      event?.stopPropagation();
      setState((prevState) => (prevState === "hidden" ? "showing" : "hidden"));
    },
    []
  );

  const handleHide = React.useCallback(() => {
    setState("hidden");
  }, []);

  React.useEffect(() => {
    if (state !== "showing") return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      handleHide();
    }

    // Capture phase ensures Escape is handled before EventBoundary stops propagation.
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [handleHide, state]);

  const showing = state === "showing";
  const childrenProps = React.useMemo(
    () => ({
      innerRef: triggerRef,
      triggerProps: {
        onClick: handleClick,
        "aria-expanded": showing,
        "aria-haspopup": "menu" as const,
      },
    }),
    [handleClick, showing]
  );

  return (
    <React.Fragment>
      {state === "showing" && (
        <TransparentUnderlay onMouseDown={swallowEvent} onClick={handleHide} />
      )}
      {state !== "hidden" && (
        <Portal>
          <Wrapper>
            <FocusTrap
              active={state === "showing"}
              focusTrapOptions={{
                fallbackFocus: () => focusTrapRef.current as HTMLElement,
                clickOutsideDeactivates: true,
                escapeDeactivates: true,
                onDeactivate: handleHide,
                returnFocusOnDeactivate: true,
              }}
            >
              <div ref={focusTrapRef} tabIndex={-1}>
                <Placement triggerRef={triggerRef} placement={props.placement}>
                  {props.body}
                </Placement>
              </div>
            </FocusTrap>
          </Wrapper>
        </Portal>
      )}
      {props.children(childrenProps)}
    </React.Fragment>
  );
}

export function calculatePlacementPosition(
  triggerRect: DOMRect,
  wrapperRect: DOMRect,
  placement: TPlacement
) {
  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  let x = triggerCenter - wrapperRect.width / 2;
  let y = triggerRect.y - wrapperRect.height + window.scrollY - 6;
  const maxLeft = window.innerWidth - wrapperRect.width - 2;
  const maxTop = window.innerHeight - wrapperRect.height - 2;

  if (placement === "top-start") {
    y = triggerRect.y - wrapperRect.height + window.scrollY;
    x = triggerRect.x;
  }

  const left = Math.min(Math.max(2, x), maxLeft) + window.scrollX;
  const top = Math.min(Math.max(2, y), maxTop) + window.scrollY;

  return { left, top };
}

type PlacementProps = {
  placement?: TPlacement;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  children: React.ReactNode;
};

function Placement({ placement, triggerRef, children }: PlacementProps) {
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = React.useState<{ left: number; top: number } | null>(
    null
  );

  const updatePosition = React.useCallback(() => {
    const wrapper = wrapperRef.current;
    const trigger = triggerRef.current;

    if (!wrapper || !trigger) return;

    const wrapperRect = wrapper.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const isValidSize = wrapperRect.width > 0 && wrapperRect.height > 0;

    if (!isValidSize) return;

    const { left, top } = calculatePlacementPosition(
      triggerRect,
      wrapperRect,
      placement
    );

    setPos({ left, top });
  }, [placement, triggerRef]);

  React.useEffect(() => {
    window.addEventListener("resize", updatePosition);

    const wrapper = wrapperRef.current;

    /**
     * Since children of wrapperRef are lazily initialized,
     * wrapper's width and height are 0 until the children are initialized.
     * We need to wait for wrapper.getBoundingClientRect to return valid values.
     */
    const resizeObserver = new ResizeObserver(updatePosition);
    if (wrapper && resizeObserver) resizeObserver.observe(wrapper);

    return () => {
      window.removeEventListener("resize", updatePosition);
      resizeObserver?.disconnect();
    };
  }, [triggerRef, updatePosition]);

  const style = React.useMemo(
    () => ({
      position: "absolute" as const,
      top: (pos?.top ?? -99999).toString() + "px",
      left: (pos?.left ?? -99999).toString() + "px",
      visibility: pos ? ("visible" as const) : ("hidden" as const),
    }),
    [pos]
  );

  return (
    <div ref={wrapperRef} style={style}>
      {children}
    </div>
  );
}

const Wrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: var(--z-index-low);
`;

const TransparentUnderlay = styled.div`
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: var(--z-index-lowest);
`;
